import { useContext, useEffect, useRef } from "react";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { GetProcessStatusApi } from "@/features/accounts/ocr/services/get-process-status-api";
import { OcrProcessStatus } from "@/shared/config/globals";
import { ErrorDialogContext } from "@/shared/context/error-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";

export const useOcrPolling = () => {
  const { accounts, setAccounts, setSelectedAccount } = useAccountGroupsContext();
  const { setError } = useContext(ErrorDialogContext);
  const pollingRef = useRef<number | null>(null);

  useEffect(() => {
    // 処理中の口座を抽出
    const processingAccounts = accounts.filter(
      (acc) => acc.ocrProcessStatus === OcrProcessStatus.Processing && acc.ocrProcessId,
    );

    // 処理中の口座がない場合、ポーリング停止
    if (processingAccounts.length === 0) {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // ポーリング関数
    const poll = async () => {
      const api = new GetProcessStatusApi();

      for (const account of processingAccounts) {
        try {
          const res = await api.GetProcessStatus({ ocr_process_id: account.ocrProcessId });

          // ステータスが変わっていたら更新 (Processing以外になったら)
          if (res.process_status !== OcrProcessStatus.Processing) {
            setAccounts((prev) =>
              prev.map((a) =>
                a.mlinkAccountId === account.mlinkAccountId &&
                a.ocrProcessId === account.ocrProcessId
                  ? { ...a, ocrProcessStatus: res.process_status }
                  : a,
              ),
            );

            // 選択中の口座と一致する場合は、選択中の口座情報も更新する
            setSelectedAccount((prev) => {
              if (
                prev &&
                prev.mlinkAccountId === account.mlinkAccountId &&
                prev.ocrProcessId === account.ocrProcessId
              ) {
                return { ...prev, ocrProcessStatus: res.process_status };
              }
              return prev;
            });

            // 異常終了・タイムアウト時は警告ダイアログを表示する
            if (
              res.process_status === OcrProcessStatus.Error ||
              res.process_status === OcrProcessStatus.Timeout
            ) {
              console.log("警告ダイアログ表示: ", res.user_message);
              setError(
                new MoneyLinkWarning({
                  title: "エラー",
                  message: res.user_message,
                }),
              );
            }
          }
        } catch (e) {
          console.error("Polling error for account:", account.mlinkAccountId, e);
        }
      }
    };

    // ポーリング開始 (既に動いていない場合)
    if (!pollingRef.current) {
      pollingRef.current = window.setInterval(poll, 10000); // 10秒ごとに確認
    }

    // クリーンアップ
    return () => {
      if (pollingRef.current) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [accounts, setAccounts, setSelectedAccount]);
};
